import type { GradientGrid, GradientStyle } from '#types/styles'

/**
 * Supported vertical anchor positions for CMS gradients.
 * @category Utils
 */
export type GradientPosition = 'top' | 'bottom' | 'center'

/**
 * Supported horizontal alignment values for center-anchored CMS gradients.
 * @category Utils
 */
export type GradientAlignment = 'left' | 'center' | 'right' | undefined

const semanticGradientTones = {
  dark: '#2A2A2A',
  light: '#FFFFFF',
} as const

const hexColorPattern = /^#(?:[\dA-F]{3}|[\dA-F]{4}|[\dA-F]{6}|[\dA-F]{8})$/i
const rgbColorPattern = /^rgba?\(([^)]+)\)$/i
const hslColorPattern = /^hsla?\(([^)]+)\)$/i
const cssVariablePattern = /^var\(.+\)$/

/**
 * CSS `to …` directions for each CMS `gradientGrid` origin.
 * Darker tone starts at the named corner/edge and fades toward the opposite side.
 * @category Utils
 */
export const gradientGridDirections = {
  'bottom-left': 'to top right',
  'bottom-right': 'to top left',
  'top-left': 'to bottom right',
  'top-right': 'to bottom left',
  'bottom-top': 'to top',
  'top-bottom': 'to bottom',
  'left-right': 'to right',
  'right-left': 'to left',
} as const satisfies Record<GradientGrid, string>

/**
 * Soft end stop so full-bleed grid overlays keep the opposite side readable.
 * @category Utils
 */
export const GRID_GRADIENT_FADE_STOP = '65%'

/**
 * Maps supported CMS gradient styles to their image coverage percentages.
 * @category Utils
 */
export const gradientHeights = {
  '35-of-image': 35,
  '50-of-image': 50,
  '100-of-image': 100,
  'full': 35,
  'focused': 50,
  'grid-gradient': 100,
  'text-area-only': 100,
} satisfies Record<Exclude<GradientStyle['gradientStyle'], 'off' | undefined>, number>

/**
 * Restricts `text-area-only` rendering when the feature flag is on for the active layer.
 * Brand enablement lives in brand-layer app config (`enableTextAreaOnlyGradient`), not an allow-list.
 * @param enabled - Feature flag from app config (`enableTextAreaOnlyGradient`).
 * @param gradientStyle - CMS gradient style token.
 * @returns `true` when the flag is on and the style is `text-area-only`.
 * @example
 * ```ts
 * isTextAreaOnlyGradientEnabled(true, 'text-area-only') // true
 * isTextAreaOnlyGradientEnabled(false, 'text-area-only') // false
 * ```
 * @category Utils
 */
export const isTextAreaOnlyGradientEnabled = (
  enabled: boolean,
  gradientStyle?: GradientStyle['gradientStyle']
) => enabled && gradientStyle === 'text-area-only'

/**
 * Type-guard for CMS `gradientGrid` origin values.
 * @param value - Candidate grid origin from CMS (may be missing or invalid).
 * @returns `true` when `value` is a known `GradientGrid` key.
 * @example
 * ```ts
 * isValidGradientGrid('bottom-left') // true
 * isValidGradientGrid('diagonal') // false
 * ```
 * @category Utils
 */
export const isValidGradientGrid = (value?: string): value is GradientGrid =>
  !!value && value in gradientGridDirections

/**
 * Restricts `grid-gradient` when the feature flag is on and `gradientGrid` is valid.
 * Brand enablement lives in brand-layer app config (`enableGridGradient`), not an allow-list.
 * Missing / invalid grid should fall back to off at the call site.
 * @param enabled - Feature flag from app config (`enableGridGradient`).
 * @param gradientStyle - CMS gradient style token.
 * @param gradientGrid - Optional CMS grid origin; required for the feature to enable.
 * @returns `true` only when the flag, style, and grid are all valid for grid-gradient rendering.
 * @example
 * ```ts
 * isGridGradientEnabled(true, 'grid-gradient', 'bottom-left') // true
 * isGridGradientEnabled(false, 'grid-gradient', 'bottom-left') // false
 * ```
 * @category Utils
 */
export const isGridGradientEnabled = (
  enabled: boolean,
  gradientStyle?: GradientStyle['gradientStyle'],
  gradientGrid?: string
) =>
  enabled
  && gradientStyle === 'grid-gradient'
  && isValidGradientGrid(gradientGrid)

/**
 * Builds the full-bleed linear-gradient background CSS for `grid-gradient`.
 * @param gradientGrid - Valid CMS grid origin controlling fade direction.
 * @param tone - Resolved opaque gradient color (see {@link resolveGradientTone}).
 * @param transparentTone - Transparent end color (see {@link getTransparentGradientTone}).
 * @returns A CSS `linear-gradient(...)` value ending at {@link GRID_GRADIENT_FADE_STOP}.
 * @example
 * ```ts
 * getGridGradientBackground('bottom-left', '#2A2A2A', '#2A2A2A00')
 * // 'linear-gradient(to top right, #2A2A2A 0%, #2A2A2A00 65%)'
 * ```
 * @category Utils
 */
export const getGridGradientBackground = (
  gradientGrid: GradientGrid,
  tone: string,
  transparentTone: string
) =>
  `linear-gradient(${gradientGridDirections[gradientGrid]}, ${tone} 0%, ${transparentTone} ${GRID_GRADIENT_FADE_STOP})`

/**
 * Normalizes CMS gradient tones into safe CSS color values.
 * @param tone - Raw CMS tone (`light` / `dark`, hex, rgb(a), hsl(a), `transparent`, `currentColor`, or `var(...)`).
 * @returns A CSS color string safe to use in gradients; invalid input becomes `'transparent'`.
 * @category Utils
 */
export const resolveGradientTone = (tone?: string) => {
  const normalizedTone = tone?.trim()

  if (!normalizedTone)
    return 'transparent'

  const semanticTone = semanticGradientTones[normalizedTone as keyof typeof semanticGradientTones]
  if (semanticTone)
    return semanticTone

  if (
    normalizedTone === 'transparent'
    || normalizedTone === 'currentColor'
    || cssVariablePattern.test(normalizedTone)
    || hexColorPattern.test(normalizedTone)
    || rgbColorPattern.test(normalizedTone)
    || hslColorPattern.test(normalizedTone)
  ) {
    return normalizedTone
  }

  return 'transparent'
}

/**
 * Produces a transparent version of a normalized gradient tone.
 * @param tone - Raw or already-normalized tone passed through {@link resolveGradientTone}.
 * @returns A zero-alpha variant of the tone, or `'transparent'` when conversion is not possible.
 * @category Utils
 */
export const getTransparentGradientTone = (tone?: string) => {
  const resolvedTone = resolveGradientTone(tone)

  if (resolvedTone === 'transparent')
    return 'transparent'

  if (resolvedTone === 'currentColor' || cssVariablePattern.test(resolvedTone))
    return 'transparent'

  if (resolvedTone.startsWith('#')) {
    if (resolvedTone.length === 4)
      return `${resolvedTone}0`
    if (resolvedTone.length === 5)
      return `${resolvedTone.slice(0, 4)}0`
    if (resolvedTone.length === 7)
      return `${resolvedTone}00`
    if (resolvedTone.length === 9)
      return `${resolvedTone.slice(0, 7)}00`
  }

  const rgbMatch = resolvedTone.match(rgbColorPattern)
  if (rgbMatch) {
    const [red = '0', green = '0', blue = '0'] = rgbMatch[1].split(',').map((value) => value.trim())
    return `rgba(${red}, ${green}, ${blue}, 0)`
  }

  const hslMatch = resolvedTone.match(hslColorPattern)
  if (hslMatch) {
    const [hue = '0', saturation = '0%', lightness = '0%'] = hslMatch[1].split(',').map((value) => value.trim())
    return `hsla(${hue}, ${saturation}, ${lightness}, 0)`
  }

  return 'transparent'
}

/**
 * Resolves the rendered gradient height for both legacy and new CMS styles.
 * @param gradientStyle - CMS gradient style token.
 * @param breakpointExtent - Measured CSS length for `text-area-only` (e.g. `'120px'`); ignored for other styles.
 * @returns A CSS height string (`'%'` or `'px'`). Missing / `off` styles return `'0%'`.
 * @category Utils
 */
export const getGradientHeight = (
  gradientStyle?: GradientStyle['gradientStyle'],
  breakpointExtent?: string
) => {
  if (gradientStyle === 'text-area-only')
    return breakpointExtent || '0px'

  if (!gradientStyle || gradientStyle === 'off')
    return '0%'

  return `${gradientHeights[gradientStyle] || 0}%`
}

/**
 * Collapses text-area-only gradients to top/bottom vertical origins.
 * @param pos - Requested CMS position; non-bottom values (including `center` / missing) map to `'top'`.
 * @returns `'top'` or `'bottom'` for text-area-only placement.
 * @category Utils
 */
export const getTextAreaOnlyPosition = (
  pos?: GradientPosition
): Extract<GradientPosition, 'top' | 'bottom'> => pos === 'bottom' ? 'bottom' : 'top'
